import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import baseURL from "../../config";

const VacancyBasedCVsList = ({ uploadedById }) => {
  const [vacancyCvs, setVacancyCvs] = useState([]);
  const [filteredCvs, setFilteredCvs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRank, setSelectedRank] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    const fetchVacancyCvs = async () => {
      setLoading(true);
      try {
        const userId = uploadedById || localStorage.getItem("user id");
        if (!userId) {
          setError("User ID not found");
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `${baseURL}/api/vacancyBasedCrewCVs/crewingagent/${userId}`
        );

        const data = response.data.data || [];
        setVacancyCvs(data);
        setFilteredCvs(data);
      } catch (err) {
        console.error("API error:", err.response || err.message);
        setError(
          err.response?.data?.message || "Failed to fetch vacancy-based CVs"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchVacancyCvs();
  }, [uploadedById]);

  // Get unique ranks for filter dropdown
  const ranks = [
    ...new Set(
      vacancyCvs.map((item) => item.rank || item.vacancyId?.rank).filter(Boolean)
    ),
  ];

  // Filter CVs based on search and rank
  useEffect(() => {
    let filtered = vacancyCvs;
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.crewName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.rank?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.vacancyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.vacancyId?.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (selectedRank) {
      filtered = filtered.filter(
        (item) =>
          (item.rank || item.vacancyId?.rank) === selectedRank
      );
    }
    setFilteredCvs(filtered);
  }, [searchTerm, selectedRank, vacancyCvs]);

  if (loading)
    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress color="primary" />
      </Box>
    );

  if (error)
    return (
      <Box textAlign="center" mt={5}>
        <Typography color="error">{error}</Typography>
      </Box>
    );

  return (
    <Box mt={isMobile ? 2 : 4} px={isMobile ? 1 : 4}>
      <Typography
        variant={isMobile ? "h5" : "h4"}
        gutterBottom
        textAlign="center"
        sx={{
          fontWeight: "bold",
          color: "red",
          letterSpacing: 1,
        }}
      >
        Selected CVs
      </Typography>

      {/* Filters */}
      <Box
        mb={3}
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        alignItems="center"
        justifyContent="center"
        gap={2}
      >
        <TextField
          variant="outlined"
          placeholder="Search by crew, rank, or vacancy..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: isMobile ? "100%" : 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" sx={{ width: isMobile ? "100%" : 200 }}>
          <InputLabel>Filter by Rank</InputLabel>
          <Select
            value={selectedRank}
            onChange={(e) => setSelectedRank(e.target.value)}
            label="Filter by Rank"
          >
            <MenuItem value="">All Ranks</MenuItem>
            {ranks.map((rank) => (
              <MenuItem key={rank} value={rank}>
                {rank}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {filteredCvs.length === 0 ? (
        <Box textAlign="center" mt={5}>
          <Typography>No vacancy-based CVs found.</Typography>
        </Box>
      ) : (
        <Grid container spacing={isMobile ? 2 : 3}>
          {filteredCvs.map((item) => (
            <Grid item xs={12} sm={12} md={6} key={item._id}>
              <Card
                variant="outlined"
                sx={{
                  transition: "0.3s",
                  borderRadius: 3,
                  boxShadow: 2,
                  background:
                    "linear-gradient(135deg, #e3f2fd 0%, #fff 100%)",
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-4px) scale(1.02)",
                    background:
                      "linear-gradient(135deg, #bbdefb 0%, #fff 100%)",
                  },
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="red"
                    gutterBottom
                  >
                    {item.vacancyName || item.vacancyId?.title || "N/A"}
                  </Typography>
                
                  <Divider sx={{ my: 1 }} />

                  <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                    <Chip
                      label={`Rank: ${item.rank || item.vacancyId?.rank || "N/A"}`}
                      color="info"
                      size="small"
                    />
                    <Chip
                      label={`Crew: ${item.crewName || "N/A"}`}
                      color="primary"
                      size="small"
                    />
                  
                    <Chip
                      label={`Location: ${item.vacancyId?.location || "N/A"}`}
                      color="success"
                      size="small"
                    />
                    <Chip
                      label={`Salary: ${
                        item.vacancyId?.salary
                          ? `${item.vacancyId.salary.min} - ${item.vacancyId.salary.max} ${item.vacancyId.salary.currency}`
                          : "N/A"
                      }`}
                      color="warning"
                      size="small"
                    />
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="subtitle2" fontWeight="bold">
                    Status History:
                  </Typography>
                  {item.statusHistory && item.statusHistory.length > 0 ? (
                    item.statusHistory.map((status) => (
                      <Box key={status._id} mb={0.5}>
                        <Chip
                          label={`${status.statusType || "Update"} (${new Date(
                            status.updatedAt
                          ).toLocaleDateString()})`}
                          color="success"
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                        <Typography variant="body2" color="textSecondary">
                          Remark: {status.remark || "-"}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2">No status updates</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default VacancyBasedCVsList;